import './_styles.scss';
import './_styles.dark-mode.scss';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { FormControl, FormGroup, Button, Alert } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import axios from 'axios';
import qs from 'qs';

import Loader from '../../../components/Loaders/Loader';
import FilterGroup from '../../../components/Filters/FilterGroup';
import Totals from '../../../components/Totals';
import ProvidersTable from './components/Table';
import ModalsManager from './components/Modals/Manager';

import { addProvider, getProvidersBalance, getProvidersList, deleteProvider, editProvider, apiKeyLog } from '../../../services/endpoints/settings';
import { settingsSelector } from '../../../redux/selectors/settings';
import { BALANCE_REQUEST_DURATION, BALANCE_REQUEST_INTERVAL, toFixed2 } from './helpers';

let timeoutDuration = null;
let intervalRequest = null;
let cancelTokenBalance = axios.CancelToken.source();
const Providers = () => {
  const location = useLocation();

  let searchRef = useRef(null);
  const selectedItem = useRef({});

  const queryString = qs.parse(location.search.slice(1));

  const reports = useSelector((state) => settingsSelector(state));
  const { mainConfig: config } = reports;
  const { access } = config;

  const [data, setData] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [openModal, setOpenModal] = useState('');
  const [modalError, setModalError] = useState('');
  const [isLoading, setIsLoading] = useState({
    page: true,
    submit: false,
    modal: false,
    modalSubmit: false,
    balance: true,
  });

  const filteredItems = useMemo(() => {
    const providersAfterFilter = data?.providers?.filter((item) => (item?.name + item?.alias || '').replace(/\s/g, '').toLowerCase().includes(searchValue.replace(/\s/g, '').toLowerCase()));
    if (providersAfterFilter && queryString.hasOwnProperty('sort') && (queryString.sort === 'balance-desc' || queryString.sort === 'balance')) {
      const separationList = providersAfterFilter.reduce(
        (acc, item) => {
          if (item?.converted_balance || item?.balance?.balance) {
            acc.balance.push(item);
          } else {
            acc.another.push(item);
          }
          return acc;
        },
        {
          balance: [],
          another: [],
        },
      );

      const anotherList = separationList.another.sort((a, b) => {
        if (b.balance_is_available) {
          return -1;
        }
        if (a.fetch_balance) {
          return -1;
        }
        return 1;
      });

      const balanceSort = separationList.balance.sort((a, b) => {
        if (a.converted_balance === null) {
          return 1;
        }
        if (b.converted_balance === null) {
          return -1;
        }
        if (Number(a.converted_balance) < Number(b.converted_balance)) {
          return queryString.sort === 'balance-desc' ? 1 : -1;
        }
        if (Number(a.converted_balance) > Number(b.converted_balance)) {
          return queryString.sort === 'balance-desc' ? -1 : 1;
        }
        return 0;
      });
      return [...balanceSort, ...anotherList];
    }
    return providersAfterFilter;
  }, [data.providers, searchValue, queryString]);

  const totals = useMemo(() => {
    if (!data?.currencies) return;

    const available = data?.providers?.filter(
      (item) => item?.balance
        && item?.balance_is_available
        && (data?.currencies?.rates?.[item?.balance?.currency] || data?.currencies?.rates?.[item.provider_currency]),
    );
    const formated = available
      ?.reduce((acc, item) => {
        if (item?.balance) {
          const checkCurrency = acc?.find((accItem) => accItem.currency === item?.balance?.currency || accItem.currency === item.provider_currency);
          if (checkCurrency) {
            return acc?.map((accItem) => {
              if (accItem.currency === checkCurrency.currency) {
                return { ...accItem, count: accItem.count + 1, value: toFixed2(String(Number(accItem.value) + Number(item?.balance?.balance))) };
              }
              return accItem;
            });
          }
          return [...acc, { count: 1, currency: item?.balance?.currency || item.provider_currency, value: toFixed2(item?.balance?.balance) }];
        }
        return acc;
      }, [])
      ?.map((item) => ({ ...item, template: data?.currencies?.currency_templates?.[item.currency]?.replace('{{value}}', item.value || '0.00') }))
      ?.sort((a, b) => {
        if (a.count < b.count) {
          return 1;
        }
        if (a.count > b.count) {
          return -1;
        }
        return 0;
      });

    const computeTotal = formated?.length
      ? formated?.reduce((acc, item) => {
        if (item.currency !== data?.currencies?.panel_currency) {
          acc += Number(item.value) * Number(data?.currencies?.rates?.[item.currency]);
        } else {
          acc += Number(item.value);
        }
        return acc;
      }, 0)
      : '0.00';
    const fixedTotal = toFixed2(computeTotal);
    const totalTemp = data?.currencies?.currency_templates?.[data?.currencies?.panel_currency]?.replace('{{value}}', fixedTotal || '0.00') || '0.00';
    const isApproximately = computeTotal === '0.00' ? false : formated?.length > 1 ? true : !formated?.every((item) => item?.currency === data?.currencies?.panel_currency);

    return {
      length: available?.length,
      items: formated,
      total: totalTemp,
      isApproximately,
    };
  }, [data.currencies, data.providers]);

  const onHideModals = () => {
    setOpenModal('');
    setModalError('');
    setIsLoading((prevState) => ({ ...prevState, modal: false }));
  };

  const clearBalance = () => {
    clearInterval(intervalRequest);
    clearTimeout(timeoutDuration);
    cancelTokenBalance.cancel('Cancel request');
    cancelTokenBalance = axios.CancelToken.source();
  };

  const clearBalanceFetch = () => {
    setData((prevState) => ({
      ...prevState,
      providers: prevState?.providers?.map((item) => ({
        ...item,
        fetch_balance: false,
      })),
    }));
  };

  const fetchBalance = async (ids) => {
    try {
      const response = await getProvidersBalance({ ids }, cancelTokenBalance.token);
      if (response.success) {
        if (response?.data?.length) {
          setData((prevState) => ({
            ...prevState,
            providers: prevState?.providers?.map((item) => {
              const finded = response?.data?.find((i) => String(i?.provider_id) === String(item?.user_provider_id));
              if (finded) {
                return {
                  ...item,
                  balance: {
                    balance: finded?.balance,
                    currency: finded?.currency,
                  },
                  converted_balance: finded?.converted_balance,
                  balance_is_available: !!finded?.balance,
                  fetch_balance: false,
                  provider_currency: finded?.currency || item?.provider_currency,
                };
              }
              return item;
            }),
          }));
        }
      }
    } catch (error) {
      clearInterval(intervalRequest);
      clearTimeout(timeoutDuration);
      clearBalanceFetch();
    }
  };

  const runBalanceDelay = useCallback(
    (balanceLoaderItems) => {
      if (balanceLoaderItems?.length && access?.providers_balance) {
        fetchBalance(balanceLoaderItems?.map((item) => item?.user_provider_id));
        intervalRequest = setInterval(() => fetchBalance(balanceLoaderItems?.map((item) => item?.user_provider_id)), BALANCE_REQUEST_INTERVAL);
        timeoutDuration = setTimeout(() => {
          clearInterval(intervalRequest);
          clearBalanceFetch();
        }, BALANCE_REQUEST_DURATION);
      } else {
        clearInterval(intervalRequest);
        clearTimeout(timeoutDuration);
        clearBalanceFetch();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const fetchList = async (loader = true) => {
    loader && setIsLoading((prevState) => ({ ...prevState, page: true }));
    try {
      const response = await getProvidersList(queryString);
      if (response.success) {
        setData(response.data);
        runBalanceDelay(response?.data?.providers?.filter((item) => !item?.balance_is_available && item?.fetch_balance));
      }
      loader && setIsLoading((prevState) => ({ ...prevState, page: false }));
    } catch (error) {
      loader && setIsLoading((prevState) => ({ ...prevState, page: false }));
    }
  };

  const onClearSearch = () => {
    setSearchValue('');
    searchRef?.focus();
  };

  const onEdit = (item) => {
    selectedItem.current = item;
    setOpenModal('edit');
  };

  const onSendAdd = async (link) => {
    setIsLoading((prevState) => ({ ...prevState, modalSubmit: true }));
    try {
      const response = await addProvider({ link });
      setIsLoading((prevState) => ({ ...prevState, modalSubmit: false }));
      if (response.success) {
        onEdit(response.data);
      }
      clearBalance();
      fetchList();
    } catch (error) {
      setIsLoading((prevState) => ({ ...prevState, modalSubmit: false }));
      setModalError(error?.data?.error_message ?? 'Something went wrong');
    }
  };

  const onSendEdit = async (data) => {
    setIsLoading((prevState) => ({ ...prevState, modal: true }));
    try {
      await editProvider(selectedItem.current?.id, data);
      setIsLoading((prevState) => ({ ...prevState, modal: false }));
      onHideModals('');
      clearBalance();
      fetchList();
    } catch (error) {
      setModalError(error?.data?.error_message ?? 'Something went wrong');
      setIsLoading((prevState) => ({ ...prevState, modal: false }));
    }
  };

  const onDelete = async () => {
    setIsLoading((prevState) => ({ ...prevState, modal: true }));
    try {
      await deleteProvider(selectedItem.current?.id);
      setIsLoading((prevState) => ({ ...prevState, modal: false }));
      onHideModals('');
      setSearchValue('');
      clearBalance();
      fetchList();
    } catch (error) {
      setModalError(error?.data?.error_message ?? 'Something went wrong');
      setIsLoading((prevState) => ({ ...prevState, modal: false }));
    }
  };

  const onSendApiKeyLog = async () => {
    if (!access?.providers_apikey_log) return;
    try {
      await apiKeyLog(selectedItem.current?.id);
    } catch (error) {
      console.log(error);
    }
  };

  const onSubmitModal = (type, data) => {
    switch (type) {
    case 'add':
      onSendAdd(data);
      break;

    case 'edit':
      onSendEdit(data);
      break;

    default:
      break;
    }
  };

  useEffect(() => {
    if (!isLoading.page && data?.providers?.every((item) => !item.fetch_balance)) {
      setIsLoading((prevState) => ({ ...prevState, balance: false }));
    }
  }, [data, isLoading.page]);

  useEffect(() => {
    fetchList();
    document.title = 'Providers';

    return () => {
      clearBalance();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="settings-providers">
      <ModalsManager
        open={openModal}
        error={modalError}
        item={selectedItem.current}
        isLoading={isLoading.modal}
        isLoadingSubmit={isLoading.modalSubmit}
        onHide={onHideModals}
        onSubmit={onSubmitModal}
        onDelete={onDelete}
        onSendLog={onSendApiKeyLog}
      />

      {isLoading.page ? (
        <div className="loader-container" style={{ minHeight: '300px' }}>
          <Loader />
        </div>
      ) : (
        <>
          <FilterGroup>
            {access?.providers_add ? (
              <Button onClick={() => setOpenModal('add')} className="pull-left add-btn">
                Add provider
              </Button>
            ) : null}
            {access?.providers_search ? (
              <li className="pull-right nav-tabs__search">
                <FormGroup className="mb-0">
                  <div className="form-control__with-clear form-control__with-search">
                    <label htmlFor="providers-search" className="search-input__icon">
                      <span className="fas fa-search" />
                    </label>
                    <FormControl
                      inputRef={(ref) => (searchRef = ref)}
                      type="text"
                      placeholder="Search provider"
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      autoComplete="off"
                      id="providers-search"
                    />
                    {searchValue ? (
                      <button type="button" className="clear" onClick={() => onClearSearch()}>
                        <span className="fas fa-times" />
                      </button>
                    ) : null}
                  </div>
                </FormGroup>
              </li>
            ) : null}
          </FilterGroup>

          {data?.currencies ? (
            <Totals
              styles={{ marginTop: '-12px', marginBottom: '6px' }}
              headContent={
                data?.providers?.length ? (
                  <div className="providers-totals__head">
                    <Alert
                      bsStyle="warning"
                      className={classNames('alert__custom', {
                        'animate-pulse': isLoading.balance,
                      })}
                    >
                      {isLoading.balance ? (
                        ''
                      ) : (
                        <span>
                          Balance info available for
                          {' '}
                          {totals?.length || 0}
                          {' '}
                          of
                          {' '}
                          {data?.providers?.length || 0}
                          {' '}
                          providers
                        </span>
                      )}
                    </Alert>
                  </div>
                ) : null
              }
              content={(
                <ul className="providers-totals">
                  {isLoading.balance ? (
                    <>
                      <div className="animate-pulse providers-totals__item providers-totals__item-loader">
                        <div className="w-35" />
                        <div className="rounded" />
                      </div>
                      <div className="animate-pulse providers-totals__item providers-totals__item-loader">
                        <div className="w-60" />
                        <div className="rounded" />
                      </div>
                      <div className="animate-pulse providers-totals__item providers-totals__item-loader mt-12">
                        <div className="w-35" />
                        <div className="rounded" />
                      </div>
                    </>
                  ) : (
                    <>
                      {totals?.items?.map((item) => (
                        <li key={item?.currency} className="providers-totals__item">
                          <span className="item-title">
                            Total
                            {' '}
                            {item?.currency}
                            {' '}
                            balance (
                            {item?.count}
                            {' '}
                            providers)
                          </span>
                          <span>{item?.template}</span>
                        </li>
                      ))}
                      {totals?.items?.length ? <hr /> : null}
                      <li className="providers-totals__item providers-totals__item-total">
                        <span className="item-title">Total provider balance</span>
                        <span>
                          <b>
                            {totals?.isApproximately && <span>~ </span>}
                            {totals?.total}
                          </b>
                        </span>
                      </li>
                    </>
                  )}
                </ul>
              )}
            />
          ) : null}

          {access?.providers_list ? <ProvidersTable access={access} items={filteredItems} onEdit={onEdit} /> : false}
        </>
      )}
    </div>
  );
};

export default Providers;
